import React, { useState, useEffect } from 'react';
import ThroughputChart from '../Charts/ThroughputChart';
import { DrilldownDataService } from '../../services/DrilldownDataService';

const OverallTab = ({ selectedPort, showDetails, openDrillDown }) => {
  const [vesselCount, setVesselCount] = useState(24);
  
  const portData = {
    'port1': { // Singapore
      throughput: '185,430 T',
      vessels: '24',
      occupancy: '82%',
      turnaround: '18.7h',
      growth: '+12.5%',
      vesselChange: '3 more than last week',
      turnaroundChange: '2.1h faster than target'
    },
    // ... other ports
  };

  const data = portData[selectedPort] || portData['port1'];

  // Simulate real-time vessel count updates
  useEffect(() => {
    const interval = setInterval(() => {
      const change = Math.random() > 0.7 ? 1 : (Math.random() < 0.3 ? -1 : 0);
      setVesselCount(prev => Math.max(20, Math.min(28, prev + change)));
    }, 30000);
    
    return () => clearInterval(interval);
  }, []);

  const commodityData = [
    { id: 'containerized', name: 'Containerized', current: '85,420 T', previous: '75,920 T', growth: '+12.5%', status: 'high' },
    { id: 'dry-bulk', name: 'Dry Bulk', current: '62,150 T', previous: '57,420 T', growth: '+8.2%', status: 'high' },
    { id: 'liquid-bulk', name: 'Liquid Bulk', current: '28,360 T', previous: '30,920 T', growth: '-8.2%', status: 'low' },
    { id: 'break-bulk', name: 'Break Bulk', current: '9,500 T', previous: '9,030 T', growth: '+5.2%', status: 'medium' }
  ];

  const getStatusClass = (status) => {
    switch(status) {
      case 'high': return 'status-indicator status-high';
      case 'medium': return 'status-indicator status-medium';
      case 'low': return 'status-indicator status-low';
      default: return 'status-indicator';
    }
  };

  // Handle insight card clicks
  const handleInsightClick = (type) => {
    if (openDrillDown) {
      const insights = {
        'record-throughput': {
          title: 'Record Throughput Achieved',
          icon: 'fas fa-chart-line',
          color: '#4da6ff',
          content: 'Port has handled 1.24M tons this quarter, a 12.5% increase YoY. Containerized cargo leads with 48% share.',
          details: 'This achievement reflects improved operational efficiency and increased trade volumes. Key factors include:',
          factors: [
            'Improved crane productivity: 28.5 moves/hour',
            'Reduced turnaround time: 18.7 hours',
            'Increased vessel calls: +8% YoY',
            'Expanded storage capacity: 1.2M TEU'
          ],
          impact: 'Contributed $850M to regional economy',
          nextSteps: ['Implement AI scheduling', 'Expand container yard', 'Add 2 new berths']
        },
        'liquid-bulk-decline': {
          title: 'Liquid Bulk Decline',
          icon: 'fas fa-exclamation-triangle',
          color: '#ff6384',
          content: 'Liquid bulk traffic decreased by 8.2% this month due to reduced crude oil imports from Middle East.',
          details: 'Primary reasons for the decline include:',
          factors: [
            'Reduced crude imports: -12% from Middle East',
            'Shift to renewable energy: +25% in green energy',
            'Geopolitical factors: Trade restrictions',
            'Market prices: Oil price volatility'
          ],
          impact: 'Revenue impact: -$15M quarterly',
          mitigation: ['Diversify liquid cargo mix', 'Develop LNG terminal', 'Attract chemical shipments']
        }
      };
      openDrillDown('insight', insights[type], insights[type].title);
    }
  };

  // Handle KPI card clicks
  const handleKPIClick = (kpiType) => {
  if (openDrillDown) {
    const kpis = {
      'monthly-throughput': {
        type: 'overall-kpi',
        title: 'Monthly Throughput Analysis',
        value: data.throughput,
        growth: data.growth,
        icon: 'fas fa-weight-hanging',
        color: '#4da6ff',
        description: `Current monthly throughput with ${data.growth} growth from last month.`,
        metrics: {
          'Current Month': data.throughput,
          'Previous Month': `${parseInt(data.throughput.replace(/[^0-9]/g, '')) * 0.875} T`,
          'Growth Rate': data.growth,
          'YoY Change': '+12.5%'
        },
        breakdown: [
          { category: 'Containerized', value: '85,420 T', percentage: '46.1%' },
          { category: 'Dry Bulk', value: '62,150 T', percentage: '33.5%' },
          { category: 'Liquid Bulk', value: '28,360 T', percentage: '15.3%' },
          { category: 'Break Bulk', value: '9,500 T', percentage: '5.1%' }
        ],
        trend: 'Increasing steadily',
        forecast: 'Expected to reach 200,000 T next month',
        keyDrivers: [
          'E-commerce growth (+25% YoY)',
          'Manufacturing recovery (+8.2% output)',
          'Port efficiency improvements',
          'Increased vessel calls'
        ],
        recommendations: [
          'Maintain current operational efficiency',
          'Prepare for peak season capacity',
          'Monitor commodity mix changes'
        ]
      },
      'vessels-handled': {
        type: 'overall-kpi',
        title: 'Vessels Handled Analysis',
        value: vesselCount.toString(),
        change: data.vesselChange,
        icon: 'fas fa-ship',
        color: '#36a2eb',
        description: `Currently handling ${vesselCount} vessels, ${data.vesselChange.toLowerCase()}.`,
        vesselTypes: [
          { type: 'Container Ships', count: 15, percentage: '62.5%' },
          { type: 'Bulk Carriers', count: 6, percentage: '25%' },
          { type: 'Tankers', count: 2, percentage: '8.3%' },
          { type: 'General Cargo', count: 1, percentage: '4.2%' }
        ],
        sizeAnalysis: [
          { range: 'Small (<10,000 DWT)', count: 8 },
          { range: 'Medium (10,000-50,000 DWT)', count: 12 },
          { range: 'Large (>50,000 DWT)', count: 4 }
        ],
        avgSize: '45,000 DWT',
        efficiency: '2.1 hours faster than industry average',
        trends: [
          'Container vessel calls increasing',
          'Bulk carrier efficiency improving',
          'Tanker turnaround time reduced'
        ]
      },
      'berth-occupancy': {
        type: 'overall-kpi',
        title: 'Berth Occupancy Analysis',
        value: data.occupancy,
        icon: 'fas fa-dock',
        color: '#ff9f40',
        description: `Current berth occupancy at ${data.occupancy}.`,
        status: data.occupancy > 80 ? 'High' : 'Optimal',
        recommendation: data.occupancy > 80 ? 'Consider activating additional berths' : 'Optimal utilization',
        berthDetails: [
          { number: 'Berth 1-4', occupancy: '95%', type: 'Container', status: 'Critical' },
          { number: 'Berth 5-6', occupancy: '78%', type: 'Bulk', status: 'Normal' },
          { number: 'Berth 7', occupancy: '65%', type: 'Liquid', status: 'Good' },
          { number: 'Berth 8', occupancy: '45%', type: 'General', status: 'Available' }
        ],
        capacityAnalysis: {
          'Total Berths': '8',
          'Active Berths': '7',
          'Available Capacity': '18%',
          'Peak Utilization': '92%'
        },
        actionItems: [
          'Monitor Berth 1-4 closely',
          'Prepare Berth 8 for activation',
          'Optimize berth scheduling'
        ]
      },
      'turnaround-time': {
        type: 'overall-kpi',
        title: 'Turnaround Time Analysis',
        value: data.turnaround,
        change: data.turnaroundChange,
        icon: 'fas fa-clock',
        color: '#ff6384',
        description: `Average turnaround time of ${data.turnaround}, ${data.turnaroundChange}.`,
        comparison: 'Industry average: 20.8 hours',
        efficiencyGain: '10.1% faster than industry',
        byVesselType: [
          { type: 'Container', time: '16.2 hours', efficiency: 'Excellent' },
          { type: 'Bulk', time: '21.5 hours', efficiency: 'Good' },
          { type: 'Liquid', time: '19.8 hours', efficiency: 'Good' },
          { type: 'General', time: '22.3 hours', efficiency: 'Average' }
        ],
        factors: [
          'Efficient cargo handling operations',
          'Quick documentation processing',
          'Minimal waiting time at berth',
          'Advanced scheduling systems'
        ],
        improvements: [
          'Reduced by 2.3 hours in last 6 months',
          'Container handling improved by 15%',
          'Document processing time halved'
        ]
      }
    };
    
    openDrillDown('overall-kpi', kpis[kpiType], kpis[kpiType].title);
  }
};

  // Handle commodity table row click
  const handleCommodityClick = (commodity) => {
    if (openDrillDown) {
      const detailedData = DrilldownDataService.getCommodityDetails(commodity.id);
      openDrillDown('commodity', detailedData, `${commodity.name} Performance`);
    }
  };

  // Handle performance indicator clicks
  const handlePerformanceClick = (indicator) => {
    if (openDrillDown) {
      const indicators = {
        'ship-rate': {
          title: 'Ship Rate Analysis',
          value: '8.2',
          unit: 'ships per day',
          icon: 'fas fa-ship',
          color: '#4cd964',
          target: '8.0',
          status: 'Exceeding target',
          breakdown: ['Container: 5.2/day', 'Bulk: 2.1/day', 'Liquid: 0.9/day'],
          efficiency: '102.5% of target'
        },
        'crane-productivity': {
          title: 'Crane Productivity Analysis',
          value: '28.5',
          unit: 'T/hour',
          icon: 'fas fa-cogs',
          color: '#5ac8fa',
          comparison: 'Industry: 26.2 T/hour',
          improvement: '+8.8% YoY',
          factors: ['Operator training', 'Maintenance schedule', 'Technology upgrade']
        },
        'gate-turnaround': {
          title: 'Gate Turnaround Analysis',
          value: '24',
          unit: 'minutes',
          icon: 'fas fa-truck-moving',
          color: '#ff9500',
          target: '21 minutes',
          status: 'Needs improvement',
          bottlenecks: ['Document verification', 'Security checks', 'Traffic management'],
          actionPlan: ['Automate documentation', 'Add security lanes', 'Optimize traffic flow']
        },
        'safety-index': {
          title: 'Safety Performance',
          value: '98.7%',
          icon: 'fas fa-shield-alt',
          color: '#ff3b30',
          target: '99.0%',
          incidents: '3 minor incidents this quarter',
          initiatives: ['Safety training', 'Equipment inspection', 'Emergency drills'],
          trend: 'Improving (+0.5% YoY)'
        }
      };
      openDrillDown('performance', indicators[indicator], indicators[indicator].title);
    }
  };

  return (
    <div className="tab-content-wrapper">
{/* Key Insights */}
<div className="insights-container">
  <div 
    className="insight-card clickable"
    onClick={() => openDrillDown && openDrillDown('insight', {
      title: 'Record Throughput Achieved',
      icon: 'fas fa-chart-line',
      iconColor: '#4da6ff',
      category: 'Performance Milestone',
      summary: 'Port has handled 1.24M tons this quarter, a 12.5% increase YoY.',
      details: 'This achievement reflects improved operational efficiency and increased trade volumes. Containerized cargo leads with 48% share of total throughput.',
      keyMetrics: [
        { label: 'Quarterly Volume', value: '1.24M tons', change: '+12.5%' },
        { label: 'Container Share', value: '48%', change: '+3.2%' },
        { label: 'Efficiency Gain', value: '+8.2%', change: 'Improved' },
        { label: 'Vessel Calls', value: '285', change: '+15 vessels' }
      ],
      contributingFactors: [
        'Improved crane productivity: 28.5 moves/hour',
        'Reduced turnaround time: 18.7 hours',
        'Increased vessel calls: +8% YoY',
        'Expanded storage capacity: 1.2M TEU'
      ],
      impact: 'Contributed $850M to regional economy and created 500 new jobs',
      nextSteps: [
        'Implement AI-based scheduling system',
        'Expand container yard by 20 acres',
        'Add 2 new berths for mega-vessels',
        'Digitalize documentation process'
      ],
      timeline: 'Q3 2024 completion for expansion projects',
      confidence: 'High (based on current growth trends)'
    }, 'Record Throughput Analysis')}
  >
    <div className="insight-title"><i className="fas fa-chart-line"></i> Record Throughput Achieved</div>
    <div className="insight-content">Port has handled 1.24M tons this quarter, a 12.5% increase YoY. Containerized cargo leads with 48% share.</div>
  </div>
  
  <div 
    className="insight-card warning clickable"
    onClick={() => openDrillDown && openDrillDown('insight', {
      title: 'Liquid Bulk Decline',
      icon: 'fas fa-exclamation-triangle',
      iconColor: '#ff6384',
      category: 'Market Trend',
      summary: 'Liquid bulk traffic decreased by 8.2% this month due to reduced crude oil imports from Middle East.',
      details: 'The decline is attributed to geopolitical factors and a shift towards renewable energy sources. This trend is expected to continue through Q3 2024.',
      keyMetrics: [
        { label: 'Current Month', value: '28,360 T', change: '-8.2%' },
        { label: 'Year-to-Date', value: '112,800 T', change: '-12.5%' },
        { label: 'Market Share', value: '15.3%', change: '-2.1%' },
        { label: 'Revenue Impact', value: '-$15M', change: 'Quarterly' }
      ],
      primaryReasons: [
        'Reduced crude imports: -12% from Middle East',
        'Shift to renewable energy: +25% in green energy investment',
        'Geopolitical factors: Trade restrictions and sanctions',
        'Market prices: Oil price volatility affecting demand'
      ],
      impact: 'Revenue reduction of $15M this quarter, affecting 5% of port revenue',
      mitigationStrategies: [
        'Diversify liquid cargo mix (LNG, chemicals)',
        'Develop dedicated LNG terminal by 2025',
        'Attract chemical shipments from new markets',
        'Implement storage optimization'
      ],
      timeline: 'Recovery expected by Q1 2025',
      confidence: 'Medium (subject to market conditions)'
    }, 'Liquid Bulk Analysis')}
  >
    <div className="insight-title"><i className="fas fa-exclamation-triangle"></i> Liquid Bulk Decline</div>
    <div className="insight-content">Liquid bulk traffic decreased by 8.2% this month due to reduced crude oil imports from Middle East.</div>
  </div>
  
  <div 
    className="insight-card success clickable"
    onClick={() => openDrillDown && openDrillDown('insight', {
      title: 'Efficiency Improvement',
      icon: 'fas fa-bolt',
      iconColor: '#4cd964',
      category: 'Operational Excellence',
      summary: 'Average vessel turnaround time improved to 18.7 hours, 2.1 hours faster than industry average.',
      details: 'This improvement places the port in the top 10% globally for operational efficiency. The gains were achieved through process optimization and technology adoption.',
      keyMetrics: [
        { label: 'Current Turnaround', value: '18.7 hours', change: '-2.1 hours' },
        { label: 'Industry Average', value: '20.8 hours', change: 'Benchmark' },
        { label: 'Vessel Productivity', value: '+15%', change: 'Improved' },
        { label: 'Cost Savings', value: '$2.8M', change: 'Annual' }
      ],
      keyImprovements: [
        'Crane productivity increased by 15% to 28.5 moves/hour',
        'Gate processing time reduced by 8 minutes to 24 minutes',
        'Documentation processing 30% faster through digitalization',
        'Berth scheduling optimized using AI algorithms'
      ],
      impact: 'Saved 1,200 vessel hours this quarter, equivalent to 50 additional vessel calls',
      sustainabilityBenefits: [
        'Reduced fuel consumption: 850,000 liters saved',
        'Lower emissions: 2,200 tons CO2 reduction',
        'Improved air quality in port vicinity'
      ],
      bestPractices: [
        'Real-time vessel tracking system',
        'Automated documentation processing',
        'Predictive maintenance scheduling',
        'Cross-training of operational staff'
      ],
      timeline: 'Continuous improvement program',
      confidence: 'High (sustainable improvements)'
    }, 'Efficiency Analysis')}
  >
    <div className="insight-title"><i className="fas fa-bolt"></i> Efficiency Improvement</div>
    <div className="insight-content">Average vessel turnaround time improved to 18.7 hours, 2.1 hours faster than industry average.</div>
  </div>
  
  <div 
    className="insight-card danger clickable"
    onClick={() => openDrillDown && openDrillDown('insight', {
      title: 'Berth Capacity Alert',
      icon: 'fas fa-ship',
      iconColor: '#ff9f40',
      category: 'Capacity Management',
      summary: 'Berth occupancy at 82% - approaching optimal capacity. Consider activating additional berths.',
      details: 'Current utilization rates indicate potential congestion risks. Proactive measures are recommended to maintain service levels.',
      keyMetrics: [
        { label: 'Current Occupancy', value: '82%', change: '+5% from last month' },
        { label: 'Optimal Range', value: '70-85%', change: 'Target' },
        { label: 'Critical Threshold', value: '90%', change: 'Alert level' },
        { label: 'Available Berths', value: '1 of 8', change: 'Berth 8 available' }
      ],
      currentUtilization: [
        { berth: 'Container Berths (1-4)', occupancy: '95%', status: 'Critical' },
        { berth: 'Bulk Berths (5-6)', occupancy: '78%', status: 'High' },
        { berth: 'Liquid Berths (7)', occupancy: '65%', status: 'Normal' },
        { berth: 'General Berth (8)', occupancy: '45%', status: 'Available' }
      ],
      risks: [
        'Potential vessel waiting times increasing',
        'Reduced operational flexibility',
        'Impact on service level agreements',
        'Increased operational costs'
      ],
      immediateActions: [
        'Activate Berth 8 for container operations',
        'Optimize berth allocation algorithm',
        'Increase shift coverage during peak hours',
        'Implement dynamic pricing for priority berthing'
      ],
      longTermSolutions: [
        'Construct new container terminal (2025)',
        'Expand existing berths by 20%',
        'Implement automated mooring systems',
        'Develop offshore waiting area'
      ],
      timeline: 'Immediate action required within 30 days',
      confidence: 'High (based on current growth projections)'
    }, 'Capacity Analysis')}
  >
    <div className="insight-title"><i className="fas fa-ship"></i> Berth Capacity Alert</div>
    <div className="insight-content">Berth occupancy at 82% - approaching optimal capacity. Consider activating additional berths.</div>
  </div>
</div>
      {/* KPI Cards */}
      <div className="dashboard-cards">
        <div 
          className="card clickable"
          onClick={() => handleKPIClick('monthly-throughput')}
        >
          <div className="card-header">
            <div className="card-title">Monthly Throughput</div>
            <div className="card-icon" style={{backgroundColor: '#4da6ff'}}>
              <i className="fas fa-weight-hanging"></i>
            </div>
          </div>
          <div className="card-content">
            <div className="card-value">{data.throughput}</div>
            <div className="card-change positive">
              <i className="fas fa-arrow-up"></i>
              <span>{data.growth} from last month</span>
            </div>
          </div>
        </div>
        
        <div 
          className="card clickable"
          onClick={() => handleKPIClick('vessels-handled')}
        >
          <div className="card-header">
            <div className="card-title">Vessels Handled</div>
            <div className="card-icon" style={{backgroundColor: '#36a2eb'}}>
              <i className="fas fa-ship"></i>
            </div>
          </div>
          <div className="card-content">
            <div className="card-value">{vesselCount}</div>
            <div className="card-change positive">
              <i className="fas fa-arrow-up"></i>
              <span>{data.vesselChange}</span>
            </div>
          </div>
        </div>
        
        <div 
          className="card clickable"
          onClick={() => handleKPIClick('berth-occupancy')}
        >
          <div className="card-header">
            <div className="card-title">Berth Occupancy</div>
            <div className="card-icon" style={{backgroundColor: '#ff9f40'}}>
              <i className="fas fa-building"></i>
            </div>
          </div>
          <div className="card-content">
            <div className="card-value">{data.occupancy}</div>
            <div className="progress-bar">
              <div className="progress-fill" style={{width: data.occupancy, backgroundColor: '#ff9f40'}}></div>
            </div>
          </div>
        </div>
        
        <div 
          className="card clickable"
          onClick={() => handleKPIClick('turnaround-time')}
        >
          <div className="card-header">
            <div className="card-title">Avg. Turnaround</div>
            <div className="card-icon" style={{backgroundColor: '#ff6384'}}>
              <i className="fas fa-clock"></i>
            </div>
          </div>
          <div className="card-content">
            <div className="card-value">{data.turnaround}</div>
            <div className="card-change positive">
              <i className="fas fa-arrow-down"></i>
              <span>{data.turnaroundChange}</span>
            </div>
          </div>
        </div>
      </div>
      
      {/* Charts and Table Section */}
      <div className="charts-section">
        <div className="chart-container">
          <h3 className="section-title">
            <i className="fas fa-chart-bar"></i>
            <span>Monthly Throughput Trend</span>
          </h3>
          <div className="chart-placeholder">
            <ThroughputChart onClick={() => openDrillDown && openDrillDown('throughput-trend', {
              title: 'Throughput Trend Analysis',
              period: 'January 2023 - April 2024',
              trend: 'Steady growth observed',
              highlights: [
                'Peak: 195,000 T in March 2024',
                'Lowest: 142,000 T in July 2023',
                'Average growth: +3.2% monthly'
              ],
              seasonality: 'Q4 typically strongest'
            }, 'Throughput Trend Details')} />
          </div>
        </div>
        
        <div className="table-container">
          <h3 className="section-title">
            <i className="fas fa-table"></i>
            <span>Top Performing Commodities</span>
          </h3>
          <table className="data-table">
            <thead>
              <tr>
                <th>Commodity</th>
                <th>This Month</th>
                <th>Last Month</th>
                <th>Growth</th>
              </tr>
            </thead>
            <tbody>
              {commodityData.map((commodity, index) => (
                <tr 
                  key={index} 
                  className={index === 0 ? 'highlight clickable-row' : 'clickable-row'}
                  onClick={() => handleCommodityClick(commodity)}
                >
                  <td><strong>{commodity.name}</strong></td>
                  <td>{commodity.current}</td>
                  <td>{commodity.previous}</td>
                  <td><span className={getStatusClass(commodity.status)}>{commodity.growth}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Performance Indicators */}
      <div className="info-container" style={{marginTop:'30px'}}>
        <h3 className="section-title">
          <i className="fas fa-chart-line"></i>
          <span>Port Performance Indicators</span>
        </h3>
      
        <div className="dashboard-cards">
          <div 
            className="card clickable"
            onClick={() => handlePerformanceClick('ship-rate')}
          >
            <div className="card-header">
              <div className="card-title">Ship Rate (per day)</div>
              <div className="card-icon" style={{backgroundColor: '#4cd964'}}>
                <i className="fas fa-ship"></i>
              </div>
            </div>
            <div className="card-content">
              <div className="card-value">8.2</div>
              <div className="progress-bar">
                <div className="progress-fill" style={{backgroundColor: '#4cd964', width:'82%'}}></div>
              </div>
            </div>
          </div>
          
          <div 
            className="card clickable"
            onClick={() => handlePerformanceClick('crane-productivity')}
          >
            <div className="card-header">
              <div className="card-title">Crane Productivity</div>
              <div className="card-icon" style={{backgroundColor: '#5ac8fa'}}>
                <i className="fas fa-cogs"></i>
              </div>
            </div>
            <div className="card-content">
              <div className="card-value">28.5 T/hr</div>
              <div className="card-change positive">
                <i className="fas fa-arrow-up"></i>
                <span>+2.3 from target</span>
              </div>
            </div>
          </div>
          
          <div 
            className="card clickable"
            onClick={() => handlePerformanceClick('gate-turnaround')}
          >
            <div className="card-header">
              <div className="card-title">Gate Turnaround</div>
              <div className="card-icon" style={{backgroundColor: '#ff9500'}}>
                <i className="fas fa-truck-moving"></i>
              </div>
            </div>
            <div className="card-content">
              <div className="card-value">24 min</div>
              <div className="card-change negative">
                <i className="fas fa-arrow-up"></i>
                <span>+3 min from target</span>
              </div>
            </div>
          </div>
          
          <div 
            className="card clickable"
            onClick={() => handlePerformanceClick('safety-index')}
          >
            <div className="card-header">
              <div className="card-title">Safety Index</div>
              <div className="card-icon" style={{backgroundColor: '#ff3b30'}}>
                <i className="fas fa-shield-alt"></i>
              </div>
            </div>
            <div className="card-content">
              <div className="card-value">98.7%</div>
              <div className="progress-bar">
                <div className="progress-fill" style={{backgroundColor: '#ff3b30', width:'98.7%'}}></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OverallTab;